// Native scene and attack-time cover share body bands and physical roof/wall rays.
import { segmentHit3d, validateShape, type ShapeCoverVolume } from '../../../../../ft3d/engine/ft3d-shape-core.js';
import { abilityCoverBetween, abilityRaySource } from '../../geometry/ability-cover.js';
import { position, hasStatus } from '../../../encounter-map/ft3d/perception.js';
import { drawnCreatureVolume } from '../../../shared/physical-creature-body.js';
import { type JanusCtx, type Row, handBack, itemField, record, scalar, upper } from '../context.js';
import { type CoverTier } from '../../actions/cover-defense.js';
import { type ShapePoint3d } from '../../../../../ft3d/engine/ft3d-shape-core.js';
import { propCoverVolumes } from './prop-cover-volumes.js';
import { resolvePilotedObjectInEncounter } from '../../compile/piloted-object.js';
import { readAbilityEntryId } from '../../compile/inventory-ability-grant-helpers.js';
import { parsePaintedWallSectionSnapshot, paintedWallPhaseVolumes } from '../../../shared/painted-wall-section-state.js';
import { WATER_WALL_ID } from '../../../shared/painted-wall-rules.js';
import { statusItemId } from '../../statuses/expiry.js';

const HUNKERED = 'BE6DC590-4EB7-B297-5AC6-8E47AA4E00EF';
const BALANCE = '59C08B6D-F8D4-F9DB-81D9-0FD592094C73';
const TRANSPORT = ['FF8D219A-2A83-0EE5-A222-E748753E8D0B', 'F13F6A58-FE91-F24D-12BE-BE7CCAD81A40',
  'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12', 'DDC66C31-4093-84AF-18BB-093D924B8738'];
const BLADE_BARRIER = 'C88CB529-C244-07ED-92F4-260FCAC62992';
const PAINTED_WALLS = new Set(['98E982F2-46EF-1B81-87D2-6B0E8AFDE922', '3375068E-F4FB-C567-C7D4-D2D4B861CD10',
  'FC95B5FB-B47D-7848-F956-06663487666C', '30DF4F8A-BA72-91D3-74F6-A26FA3A54B9B',
  'B9032A2F-B68E-0D7E-A234-90CAD8959892', BLADE_BARRIER]);

export interface ContestCover { tier: CoverTier; blockedSamples: number; totalSamples: number; advStacks: number; autoWin: boolean }
const clear = (): ContestCover => ({ tier: 'none', blockedSamples: 0, totalSamples: 0, advStacks: 0, autoWin: false });

/** Pure ft_directional_cover_with_bodies() geometry and tier-to-contest mapping. */
export function contestCoverFromGeometry(shape: Row, source: ShapePoint3d, targetVolume: ShapeCoverVolume,
  props: ShapeCoverVolume[], bodies: ShapeCoverVolume[], hunkered: boolean, skillId: string): ContestCover {
  const measured = abilityCoverBetween(shape, source, targetVolume, [...props, ...bodies], { hunkered });
  let tier = measured.tier;
  if (tier === 'blocked' && bodies.length && abilityCoverBetween(shape, source, targetVolume, props, { hunkered }).tier !== 'blocked')
    tier = 'heavy';
  return { tier, blockedSamples: measured.blockedSamples, totalSamples: measured.totalSamples,
    advStacks: skillId === BALANCE && (tier === 'partial' || tier === 'heavy') ? 1 : 0,
    autoWin: tier === 'blocked' };
}

function volume(shape: Row, row: Row, catalog?: unknown): ShapeCoverVolume | null {
  return drawnCreatureVolume(row, shape, { catalog, position, hasStatus }) ?? null;
}

export function contestDirectionalCover(ctx: JanusCtx, ability: Row, caster: Row, target: Row, skillId: string): ContestCover {
  // ft_contest_cover_context() checks the ability AND janus_attack_ability_context_arr() — the nested
  // attack_ability_arr a weapon-triggered on-hit ability carries (attack-weapon-resolution.ts builds it).
  const attackContext = record(ability.attack_ability_arr);
  if (ctx.read.has(ability, 'ignores_line_of_effect')
      || (attackContext && Object.keys(attackContext).length && ctx.read.has(attackContext, 'ignores_line_of_effect'))) return clear();
  if (upper(record(caster.encounter_creatures)?.encounter_creature_id) === upper(record(target.encounter_creatures)?.encounter_creature_id)) return clear();
  const raw = record(ctx.encounter.encounters)?.encounter_shape ?? ctx.encounter.encounter_shape;
  if (raw == null || raw === '') return clear();
  let shape: Row;
  try { shape = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Row; validateShape(shape); }
  catch { handBack('contest_cover:shape-not-held'); }
  // ft_spatial_action_origin_arr(): only the currently piloted grant's own controls
  // start at the machine. Native actions keep the operator's origin.
  let originId: string | null = null;
  if (hasStatus(caster, ...TRANSPORT)) {
    const pilotedId = resolvePilotedObjectInEncounter(caster, ctx.encounter);
    if (!pilotedId) handBack('contest_cover:transport-origin-not-held');
    const usedId = upper(scalar(ctx.read.value(ability, 'item_id_used')) ?? itemField(ability, 'item_id'));
    const grant = ctx.read.entries(caster, String(itemField(ability, 'item_type') ?? '')).some(entry =>
      upper(readAbilityEntryId(entry, { itemField: (row, key) => itemField(record(row), key), resolveCarrierRef: ctx.read.definition })) === usedId
      && upper(record(entry)?.source) === pilotedId);
    if (grant) originId = pilotedId;
  }
  const sourceVolume = originId
    ? propCoverVolumes(ctx, shape, raw, [], 'contest_cover', originId)[0]
      ?? volume(shape, ctx.members.get(originId) ?? {}, ctx.encounter.catalog)
    : volume(shape, caster, ctx.encounter.catalog);
  const targetVolume = volume(shape, target, ctx.encounter.catalog);
  if (!sourceVolume || !targetVolume) handBack('contest_cover:body-not-held');
  const inventories = ctx.encounter.encounter_inventories_arr;
  if (!Array.isArray(inventories)) handBack('contest_cover:inventory-roster-not-held');
  const carriers = [ctx.encounter, ...inventories, ...(Array.isArray(ctx.encounter.encounter_creatures_arr) ? ctx.encounter.encounter_creatures_arr : [])];
  for (const carrier of carriers) {
    const row = record(carrier);
    if (!row) continue;
    if (hasStatus(row, BLADE_BARRIER)) handBack('contest_cover:painted-wall-geometry-not-held');
    // a painted wall binds inventory rows to its ribbon (ft_painted_wall_inventory_geometry_index) — its segment test is not held here
    if (inventories.length) for (const status of ctx.read.statuses(row))
      if (PAINTED_WALLS.has(upper(status.status_origin_id ?? status.status_item_id))) handBack('contest_cover:painted-wall-geometry-not-held');
  }
  // Session 1 step 9 (2026-09-29): the solid props in the line (ft_directional_cover_prop_volumes) — no longer a blanket hand-back.
  const props = inventories.length ? propCoverVolumes(ctx, shape, raw, [record(caster.encounter_creatures)?.encounter_creature_id as string,
    record(target.encounter_creatures)?.encounter_creature_id as string, originId ?? ''], 'contest_cover') : [];
  const casterId = upper(record(caster.encounter_creatures)?.encounter_creature_id);
  const targetId = upper(record(target.encounter_creatures)?.encounter_creature_id);
  const bodies: ShapeCoverVolume[] = [];
  for (const row of Array.isArray(ctx.encounter.encounter_creatures_arr) ? ctx.encounter.encounter_creatures_arr : []) {
    const member = record(row); if (!member) handBack('contest_cover:creature-roster-not-held');
    const ec = record(member.encounter_creatures);
    const id = upper(ec?.encounter_creature_id);
    if (!id || id === casterId || id === targetId || id === originId || Number(ec?.encounter_creature_private ?? 0) !== 0
      || Number(ec?.encounter_creature_deleted ?? 0) === 1) continue;
    const body = volume(shape, member, ctx.encounter.catalog);
    if (!body) handBack(hasStatus(member, ...TRANSPORT) ? 'contest_cover:transport-body-not-held' : 'contest_cover:occluder-body-not-held');
    bodies.push(body);
  }
  const source = abilityRaySource(sourceVolume);
  const hunkered = hasStatus(target, HUNKERED);
  return contestCoverFromGeometry(shape, source, targetVolume, props, bodies, hunkered, skillId);
}

/** ft_spatial_action_geometry_context() for a ranged spell attack. The same reviewed
 * body/ray classifier serves contests; unsupported scene inputs exit before a d20. */
export function spellAttackDirectionalCover(ctx: JanusCtx, caster: Row, target: Row,
  options: { melee?: boolean; strictBodies?: boolean } = {}): CoverTier {
  const raw = record(ctx.encounter.encounters)?.encounter_shape ?? ctx.encounter.encounter_shape;
  if (raw == null || raw === '') return 'none';
  let shape: Row;
  try { shape = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Row; validateShape(shape); }
  catch { handBack('attack_roll:shape-not-held'); }
  const sourceVolume = volume(shape, caster, ctx.encounter.catalog), targetVolume = volume(shape, target, ctx.encounter.catalog);
  if (!sourceVolume || !targetVolume) handBack('attack_roll:body-not-held');
  if (hasStatus(caster, ...TRANSPORT)) handBack('attack_roll:transport-origin-not-held');
  if (options.melee) {
    const center = (body: ShapeCoverVolume): ShapePoint3d => ({ x: body.x, y: body.y, z: (body.zMin + body.zMax) / 2 });
    return segmentHit3d(shape, center(sourceVolume), center(targetVolume), { mode: 'sight', containToUnion: true }) ? 'blocked' : 'none';
  }
  const inventories = ctx.encounter.encounter_inventories_arr;
  if (!Array.isArray(inventories)) handBack('attack_roll:inventory-roster-not-held');
  const props = propCoverVolumes(ctx, shape, raw, [
    record(caster.encounter_creatures)?.encounter_creature_id as string,
    record(target.encounter_creatures)?.encounter_creature_id as string], 'attack_roll');
  const carriers = [ctx.encounter, ...inventories, ...(Array.isArray(ctx.encounter.encounter_creatures_arr) ? ctx.encounter.encounter_creatures_arr : [])];
  for (const carrier of carriers) {
    const row = record(carrier);
    if (!row) continue;
    if (hasStatus(row, BLADE_BARRIER)) handBack('attack_roll:painted-wall-geometry-not-held');
    for (const status of ctx.read.statuses(row)) {
      const id = upper(status.status_origin_id ?? status.status_item_id);
      if (id === WATER_WALL_ID) {
        // Concentrating and other cast companions are not the wall's anchor.
        if (Number(status.status_aoe_source ?? 0) !== 1 && statusItemId(status) !== WATER_WALL_ID) continue;
        const snapshot = parsePaintedWallSectionSnapshot(status.status_aoe_geometry);
        // Liquid Water passes attacks; its surviving ribbon supplies weapon DIS at attack time.
        // Frozen sections retain the existing unsupported solid-wall contract.
        if (snapshot && !paintedWallPhaseVolumes(snapshot.geometry, snapshot.sections).frozen.length) continue;
      }
      if (PAINTED_WALLS.has(id)) handBack('attack_roll:painted-wall-geometry-not-held');
    }
  }
  if (!Array.isArray(ctx.encounter.encounter_creatures_arr)) handBack('attack_roll:creature-roster-not-held');
  const casterId = upper(record(caster.encounter_creatures)?.encounter_creature_id);
  const targetId = upper(record(target.encounter_creatures)?.encounter_creature_id);
  const bodies: ShapeCoverVolume[] = [];
  for (const entry of ctx.encounter.encounter_creatures_arr) {
    const member = record(entry); if (!member) handBack('attack_roll:creature-roster-not-held');
    const ec = record(member.encounter_creatures);
    const id = upper(ec?.encounter_creature_id);
    if (hasStatus(member, ...TRANSPORT)) handBack('attack_roll:transport-body-not-held');
    if (!id || id === casterId || id === targetId || Number(ec?.encounter_creature_private ?? 0) !== 0
        || Number(ec?.encounter_creature_deleted ?? 0) === 1) continue;
    const body = volume(shape, member, ctx.encounter.catalog);
    if (!body) handBack('attack_roll:occluder-body-not-held');
    bodies.push(body);
  }
  const source = abilityRaySource(sourceVolume);
  if (options.strictBodies) return abilityCoverBetween(shape, source, targetVolume, [...props, ...bodies], { hunkered: hasStatus(target, HUNKERED) }).tier;
  return contestCoverFromGeometry(shape, source, targetVolume, props, bodies, hasStatus(target, HUNKERED), '').tier;
}

/** apply_elevation_adv_dis_to_attack_roll(): support planes, angle, and the lower
 * creature's body midpoint. Shaped encounters store feet; legacy rows store centers. */
export function attackElevationAdv(ctx: JanusCtx, caster: Row, target: Row, melee: boolean): number {
  const disabled = ctx.read.value(ctx.encounter, 'disable_elevation_attack_adv_dis')
    ?? record(ctx.encounter.encounter_arr)?.disable_elevation_attack_adv_dis;
  if (!record(target.encounter_creatures) || (disabled !== null && disabled !== undefined && disabled !== false
      && disabled !== 0 && disabled !== '0' && disabled !== '')) return 0;
  if (!record(caster.encounter_creatures)) handBack('apply_elevation_adv_dis_to_attack_roll:caster-body-not-held');
  const raw = record(ctx.encounter.encounters)?.encounter_shape ?? ctx.encounter.encounter_shape;
  const shaped = raw != null && raw !== '';
  let shape: Row | null = null;
  if (shaped) {
    try { shape = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Row; validateShape(shape); }
    catch { handBack('apply_elevation_adv_dis_to_attack_roll:shape-not-held'); }
  }
  const c = position(caster), t = position(target);
  const threshold = melee ? 4 : 3;
  const readers = { catalog: ctx.encounter.catalog, clipCeiling: false, position, hasStatus };
  const casterBody = drawnCreatureVolume(caster, shape, readers);
  const targetBody = drawnCreatureVolume(target, shape, readers);
  if (!casterBody || !targetBody) handBack('apply_elevation_adv_dis_to_attack_roll:body-not-held');
  const casterHeight = casterBody.zMax - casterBody.zMin, targetHeight = targetBody.zMax - targetBody.zMin;
  const casterSupport = shaped ? casterBody.zMin : c.z - casterHeight / 2;
  const targetSupport = shaped ? targetBody.zMin : t.z - targetHeight / 2;
  const delta = casterSupport - targetSupport;
  if (Math.abs(delta) < threshold) return 0;
  const horizontal = Math.max(.01, Math.hypot(c.x - t.x, (c.y ?? 0) - (t.y ?? 0)));
  if (Math.atan2(Math.abs(delta), horizontal) * 180 / Math.PI < 12) return 0;
  const casterMid = casterSupport + casterHeight / 2;
  const targetMid = targetSupport + targetHeight / 2;
  if (delta >= threshold && casterSupport > targetMid) return 1;
  if (delta <= -threshold && targetSupport > casterMid) return -1;
  return 0;
}
